import "./styles.css";
import { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import { guestBookingSchema, NO_DISTRICT_VALUE } from "./Schema.js";
import { useBookingForm } from "./useBookingForm.js";
import { useNavigate } from "react-router-dom";
import {
  fetchAllClusters,
  fetchAllVenues,
  fetchAllFacilities,
  fetchTimeSlots,
  fetchAllStates,
  fetchAllDistrictsByState,
  checkDuplicateMobileNumber,
} from "../../services/adminApiService/venueManagerApi/venueManagerApi.js";

const formatDate = (date) => {
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const parseDate = (value) => (value ? new Date(`${value}T00:00:00`) : null);

const DUPLICATE_MOBILE_ERROR = "Mobile number already exists.";

export default function GuestForm({ selectedUser }) {
  const navigate = useNavigate();
  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleSubmit,
    resetForm,
  } = useBookingForm(guestBookingSchema);
  const [clusters, setClusters] = useState([]);
  const [venues, setVenues] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const today = new Date().toISOString().split("T")[0];
  const [timeSlots, setTimeSlots] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [mobileLookupError, setMobileLookupError] = useState("");

  // const onSubmit = handleSubmit(async (data) => {
  //   navigate("/payment-summary", {
  //     state: {
  //       bookingType: "guest",
  //       user: {
  //         name: data.fullName,
  //         mobile: data.mobileNumber,
  //         gender: data.gender,
  //         state: data.state,
  //         district: data.district,
  //       },
  //       booking: data,
  //       amount: 1200,
  //     },
  //   });
  // });

  // const hasError = (field) => Boolean(touched[field] && errors[field]);
  // const inputClass = (field) =>
  //   hasError(field) ? "guest-input guest-input-error" : "guest-input";

  // const showError = (field) =>
  //   hasError(field) ? (
  //     <span className="field-error">{errors[field]}</span>
  //   ) : null;

  useEffect(() => {
    if (
      clusters.length === 1 &&
      String(values.cluster) !== String(clusters[0].id)
    ) {
      handleChange("cluster", String(clusters[0].id));
    }
  }, [clusters, values.cluster, handleChange]);

  useEffect(() => {
    if (venues.length === 1 && String(values.venue) !== String(venues[0].id)) {
      handleChange("venue", String(venues[0].id));
    }
  }, [venues, values.venue, handleChange]);

  useEffect(() => {
    const getClusters = async () => {
      try {
        const res = await fetchAllClusters();
        setClusters(res?.data?.data || []);
      } catch (err) {
        console.error("Error fetching clusters:", err);
      }
    };

    getClusters();
  }, []);

  useEffect(() => {
    if (!values.cluster) {
      setVenues([]);
      return;
    }

    const getVenues = async () => {
      try {
        const res = await fetchAllVenues(values.cluster);
        setVenues(res?.data?.data || []);
      } catch (err) {
        console.error("Error fetching venues:", err);
      }
    };

    getVenues();
  }, [values.cluster]);

  useEffect(() => {
    if (!values.venue) {
      setFacilities([]);
      return;
    }

    const getFacilities = async () => {
      try {
        const res = await fetchAllFacilities(values.venue);
        setFacilities(res?.data?.data || []);
      } catch (err) {
        console.error("Error fetching facilities:", err);
      }
    };

    getFacilities();
  }, [values.venue]);

  const onSubmit = handleSubmit(async (data) => {
    if (participantSlotError) return;

    setMobileLookupError("");

    try {
      const duplicateMobileResponse = await checkDuplicateMobileNumber(
        data.mobileNumber,
      );
      const isMobileAlreadyRegistered =
        duplicateMobileResponse?.data?.data?.is_no_exist === true;

      if (isMobileAlreadyRegistered) {
        setMobileLookupError(DUPLICATE_MOBILE_ERROR);
        await Swal.fire({
          icon: "error",
          title: "Mobile number already exists",
          text:
            duplicateMobileResponse?.data?.message ||
            "Please select this user from Existing User.",
          confirmButtonText: "OK",
        });
        return;
      }
    } catch (error) {
      setMobileLookupError(
        "Unable to verify this mobile number right now. Please try again.",
      );
      return;
    }

    const updatedData = {
      ...data,
      sport_id: selectedFacility?.facility_type || "", // ✅ inject here
    };

    // ✅ save updated data
    localStorage.setItem("bookingFormData", JSON.stringify(updatedData));

    navigate("/payment-summary", {
      state: {
        bookingType: "guest",
        user: selectedUser,
        sportName: selectedFacility?.facility_name || "",
        zoneName: selectedZone?.zone_name || "",
        slotLabel: selectedSlot
          ? `${selectedSlot.slot_from_time} - ${selectedSlot.slot_to_time} (${selectedSlot.total_slot} left)`
          : "",
        clusterName: selectedCluster?.cluster_name || "",
        venueName: selectedVenue?.venue_name || "",
        venueAddress: selectedVenue?.venue_address || "",
        stateId: selectedState?.id || "",
        districtId: selectedDistrict?.id ?? null,
        booking: data,
        amount: totalAmount,
      },
    });
  });

  const hasError = (field) =>
    field === "mobileNumber"
      ? Boolean(mobileLookupError || (touched[field] && errors[field]))
      : field === "participants"
        ? Boolean(participantSlotError || (touched[field] && errors[field]))
        : Boolean(touched[field] && errors[field]);
  const inputClass = (field) =>
    hasError(field) ? "booking-input booking-input-error" : "booking-input";

  // Only show error after user has touched the field
  const showError = (field) =>
    field === "mobileNumber" && mobileLookupError ? (
      <span className="field-error">{mobileLookupError}</span>
    ) : field === "participants" && participantSlotError ? (
      <span className="field-error">{participantSlotError}</span>
    ) : hasError(field) ? (
      <span className="field-error">{errors[field]}</span>
    ) : null;

  const handleReset = () => {
    setMobileLookupError("");
    resetForm();
  };

  useEffect(() => {
    // ✅ only call when all required fields are present
    if (!values.venue || !values.zone || !values.bookingDate) {
      setTimeSlots([]);
      return;
    }

    const getTimeSlots = async () => {
      try {
        const res = await fetchTimeSlots(
          values.venue,
          selectedFacility?.facility_type,
          values.zone,
          values.bookingDate,
        );

        if (res?.data?.success) {
          setTimeSlots(res?.data?.data?.slots || []);
        }
      } catch (error) {
        console.error("Error fetching time slots:", error);
      }
    };

    getTimeSlots();
  }, [values.venue, values.sport, values.zone, values.bookingDate]);

  const selectedFacility = facilities.find(
    (f) => String(f.id) === String(values.sport),
  );

  const zones = selectedFacility?.zones || [];

  const selectedSlot = timeSlots.find(
    (slot) => String(slot.id) === String(values.timeSlot),
  );

  const selectedSlotRemaining =
    selectedSlot?.remaining == null ? null : Number(selectedSlot.remaining);
  const participantsValue =
    values.participants === "" || values.participants == null
      ? null
      : Number(values.participants);
  const participantSlotError =
    selectedSlotRemaining !== null &&
    participantsValue !== null &&
    participantsValue > selectedSlotRemaining
      ? `Max allowed ${selectedSlotRemaining} participants.`
      : "";

  const totalAmount =
    selectedSlot && values.participants
      ? Number(selectedSlot.ammount) * Number(values.participants)
      : "";

  const selectedZone = zones.find(
    (z) => String(z.zone_id) === String(values.zone),
  );

  const selectedCluster = clusters.find(
    (c) => String(c.id) === String(values.cluster),
  );

  const selectedVenue = venues.find(
    (v) => String(v.id) === String(values.venue),
  );

  const selectedState = states.find(
    (s) => String(s.id) === String(values.state),
  );

  const selectedDistrict = districts.find(
    (d) => String(d.id) === String(values.district),
  );

  useEffect(() => {
    const getStates = async () => {
      try {
        const res = await fetchAllStates();
        setStates(res?.data?.data || []);
      } catch (err) {
        console.error("Error fetching states:", err);
      }
    };

    getStates();
  }, []);

  useEffect(() => {
    if (!values.state) {
      setDistricts([]);
      return;
    }

    const getDistricts = async () => {
      try {
        const res = await fetchAllDistrictsByState(values.state);
        setDistricts(res?.data?.data || []);
      } catch (err) {
        console.error("Error fetching districts:", err);
      }
    };

    getDistricts();
  }, [values.state]);

  return (
    <form className="guest-wrapper" onSubmit={onSubmit}>
      {/* PERSONAL DETAILS */}
      <h3 className="guest-title">Personal Details</h3>

      <div className="guest-grid">
        <div className="guest-field">
          <label htmlFor="fullName">
            Full Name <span>*</span>
          </label>
          <input
            id="fullName"
            className={inputClass("fullName")}
            aria-invalid={hasError("fullName")}
            placeholder="Enter Full Name"
            value={values.fullName ?? ""}
            onChange={(e) => handleChange("fullName", e.target.value)}
          />
          {showError("fullName")}
        </div>

        <div className="guest-field">
          <label htmlFor="mobileNumber">
            Mobile Number <span>*</span>
          </label>
          <input
            id="mobileNumber"
            className={inputClass("mobileNumber")}
            aria-invalid={hasError("mobileNumber")}
            placeholder="Mobile Number"
            value={values.mobileNumber ?? ""}
            onChange={(e) => {
              setMobileLookupError("");
              handleChange("mobileNumber", e.target.value);
            }}
          />
          {showError("mobileNumber")}
        </div>

        <div className="guest-field">
          <label htmlFor="gender">
            Gender <span>*</span>
          </label>
          <select
            id="gender"
            className={inputClass("gender")}
            aria-invalid={hasError("gender")}
            value={values.gender ?? ""}
            onChange={(e) => handleChange("gender", e.target.value)}
          >
            <option value="">Select Gender</option>
            <option value="M">Male</option>
            <option value="F">Female</option>
            <option value="O">Other</option>
          </select>
          {showError("gender")}
        </div>

        <div className="guest-field">
          <label htmlFor="state">
            State <span>*</span>
          </label>

          <select
            id="state"
            className={inputClass("state")}
            aria-invalid={hasError("state")}
            value={values.state ?? ""}
            onChange={(e) => handleChange("state", e.target.value)}
          >
            <option value="">Select State</option>

            {states.map((state) => (
              <option key={state.id} value={state.id}>
                {state.name}
              </option>
            ))}
          </select>

          {showError("state")}
        </div>

        <div className="guest-field">
          <label htmlFor="district">
            District <span>*</span>
          </label>

          <select
            id="district"
            className={inputClass("district")}
            aria-invalid={hasError("district")}
            value={values.district ?? ""}
            onChange={(e) => handleChange("district", e.target.value)}
            disabled={!values.state}
          >
            <option value="">Select District</option>
            {districts?.length === 0 && (
              <option value={NO_DISTRICT_VALUE}>N/A</option>
            )}

            {districts.map((district) => (
              <option key={district.id} value={district.id}>
                {district.name}
              </option>
            ))}
          </select>

          {showError("district")}
        </div>
      </div>

      {/* BOOKING DETAILS */}
      <h3 className="booking-title mt-5">Booking Details</h3>

      <div className="booking-grid">
        <div className="booking-field">
          <label htmlFor="cluster">
            Cluster <span className="required">*</span>
          </label>
          {/* <select
            id="cluster"
            className={inputClass("cluster")}
            aria-invalid={hasError("cluster")}
            value={values.cluster ?? ""}
            onChange={(e) => handleChange("cluster", e.target.value)}
          >
            <option value="">Select Cluster</option>

            {clusters.map((cluster) => (
              <option key={cluster.id} value={cluster.id}>
                {cluster.cluster_name}
              </option>
            ))}
          </select> */}
          <input
            type="text"
            id="cluster"
            className={inputClass("cluster")}
            value={clusters[0]?.cluster_name || ""}
            disabled
          />
          {/* {showError("cluster")} */}
        </div>

        <div className="booking-field">
          <label htmlFor="venue">
            Venue <span className="required">*</span>
          </label>
          {/* <select
            id="venue"
            value={values.venue ?? ""}
            onChange={(e) => handleChange("venue", e.target.value)}
            className={inputClass("venue")}
            aria-invalid={hasError("venue")}
            disabled={!values.cluster || venues.length === 1}
          >
            <option value="">Select Venue</option>

            {venues.map((v) => (
              <option key={v.id} value={v.id}>
                {v.venue_name}
              </option>
            ))}
          </select> */}
          <input
            type="text"
            id="venue"
            className={inputClass("venue")}
            value={venues[0]?.venue_name || ""}
            disabled
          />
          {/* {showError("venue")} */}
          {values.cluster && venues.length === 0 && (
            <span className="field-error">No venue available</span>
          )}
        </div>

        <div className="booking-field">
          <label htmlFor="sport">
            Sport <span className="required">*</span>
          </label>
          <select
            id="sport"
            value={values.sport ?? ""}
            onChange={(e) => handleChange("sport", e.target.value)}
            className={inputClass("sport")}
            disabled={!values.venue || facilities.length == 0}
          >
            <option value="">Select Sport</option>

            {facilities.map((f) => (
              <option key={f.id} value={f.id}>
                {f.facility_name}
              </option>
            ))}
          </select>
          {showError("sport")}
          {values.venue && facilities.length === 0 && (
            <span className="field-error">No sport available</span>
          )}
        </div>

        <div className="booking-field">
          <label htmlFor="bookingDate">
            Date <span className="required">*</span>
          </label>
          <input
            type="date"
            id="bookingDate"
            className={inputClass("bookingDate")}
            aria-invalid={hasError("bookingDate")}
            value={values.bookingDate ?? ""}
            min={today}
            onChange={(e) => {
              handleChange("bookingDate", e.target.value);
              // e.target.blur();
            }}
          />
          {showError("bookingDate")}
        </div>

        <div className="booking-field">
          <label htmlFor="zone">
            Zone <span className="required">*</span>
          </label>

          <select
            id="zone"
            className={inputClass("zone")}
            aria-invalid={hasError("zone")}
            value={values.zone ?? ""}
            onChange={(e) => handleChange("zone", e.target.value)}
            disabled={!values.sport || zones.length == 0} // ✅ only enable after facility selected
          >
            <option value="">Select Zone</option>

            {zones.map((zone) => (
              <option key={zone.zone_id} value={zone.zone_id}>
                {zone.zone_name}
              </option>
            ))}
          </select>

          {showError("zone")}
          {values.sport && zones.length === 0 && (
            <span className="field-error">No zone available</span>
          )}
        </div>

        <div className="booking-field">
          <label htmlFor="timeSlot">
            Select Time Slot <span className="required">*</span>
          </label>

          <select
            id="timeSlot"
            className={inputClass("timeSlot")}
            value={values.timeSlot ?? ""}
            onChange={(e) => handleChange("timeSlot", e.target.value)}
            disabled={!timeSlots.length}
          >
            <option value="">Select Available Slot</option>

            {timeSlots.map((slot) => (
              <option
                key={slot.id}
                value={slot.id}
                disabled={slot.total_slot === 0} // 🚫 disable full slots
              >
                {slot.slot_from_time} - {slot.slot_to_time} ({slot?.remaining}{" "}
                left)
              </option>
            ))}
          </select>

          {showError("timeSlot")}

          {/* ✅ API-based validation */}
          {values.zone && values.bookingDate && timeSlots.length === 0 && (
            <span className="field-error">
              No slots available for selected date & zone
            </span>
          )}
        </div>
        <div className="booking-field">
          <label htmlFor="participants">
            No. of Participants <span className="required">*</span>
          </label>
          <input
            type="number"
            id="participants"
            placeholder="Select No. of Participants"
            className={inputClass("participants")}
            aria-invalid={hasError("participants")}
            value={values.participants ?? ""}
            disabled={!timeSlots.length}
            min={1}
            inputMode="numeric" // 📱 mobile numeric keyboard
            onKeyDown={(e) => {
              // 🚫 block invalid keys
              if (["e", "E", "+", "-", "."].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              let raw = e.target.value;

              // ✅ allow empty (so user can delete)
              if (raw === "") {
                handleChange("participants", "");
                return;
              }

              // 🚫 remove non-digits (extra safety)
              raw = raw.replace(/[^0-9]/g, "");

              let value = Number(raw);

              // 🚫 prevent 0 or negative
              if (value < 1) value = 1;

              handleChange("participants", value);
            }}
          />
          {/* {selectedSlotRemaining !== null && (
            <small className="booking-helper-text">
              Max allowed: {selectedSlotRemaining}
            </small>
          )} */}
          {showError("participants")}
        </div>
        <div className="guest-field full">
          <label>Amount</label>
          <input
            value={totalAmount ? `₹ ${totalAmount}/-` : "₹ 0/-"}
            readOnly
            className="booking-input"
          />
        </div>
      </div>

      {/* Footer Buttons */}
      <div className="booking-footer">
        <button type="button" className="btn-cancel" onClick={handleReset}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={isSubmitting}>
          {isSubmitting ? "Please Wait..." : "Continue"}
        </button>
      </div>
    </form>
  );
}
